import classNames from 'classnames';
import { useEffect, useState } from 'react';
import React from 'react';
import { Mode } from 'react-hook-form';
import Icon from '@/components/atoms/icon/icon';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import ToggleSwitch from '@/components/molecules/toggle-switch/toggle-switch';
import { TooltipPosition } from '@/components/molecules/tooltip/tooltip';
import EditPaxDetails, {
  EditedPassenger,
} from '@/components/organisms/edit-pax-details/edit-pax-details';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './edit-room-occupants.module.scss';

export interface EditRoomOccupantsProps {
  /** Text to show for agree send email checkbox */
  agreeSendEmailText?: string;
  /** Text to show for agree send sms checkbox */
  agreeSendSMSText?: string;
  /** The list of available address countries */
  availableAddressCountries?: Content.Properties.AddressCountry[];
  /** The class name to apply */
  className?: string;
  /** Whether editing the date of birth is disabled */
  disableEditDoB?: boolean;
  /** Whether editing the names is disabled */
  disableEditNames?: boolean;
  /** Text to show as disclaimer */
  disclaimerText?: string;
  /** Whether email and phone are mandatory */
  emailAndPhoneMandatory?: boolean;
  /** Whether to show the address input */
  hideAddress?: boolean;
  /** Whether to show the age input */
  hideAge?: boolean;
  /** Indicates whether the component is fetching data */
  isFetching?: boolean;
  /** Indicates whether the component is in a loading state */
  isLoading?: boolean;
  /** An event that is triggered on change */
  onChange?: (editedPassengers: EditedPassenger[]) => void;
  /** Callback for updating saved passenger selection by pax hash */
  onSelectCompanionByPaxHash?: (
    uniquePaxHash: string,
    companionId: string | null,
  ) => void;
  /** An event that is triggered on travel planner toggle */
  onTravelPlannerToggle?: (uniquePaxHash: string) => void;
  /** An event that is triggered on validation */
  onValidation?: (value: boolean) => void;
  /** The occupants of the room */
  passengers?: Content.Properties.Passenger[];
  /** The name of the room */
  roomName?: string;
  /** The number of the room */
  roomNumber?: number;
  /** Shared selected saved passenger ids by pax hash */
  selectedCompanionIdsByPaxHash?: Record<string, string>;
  /** Whether to clear the edited data */
  shouldClear?: boolean;
  /** Indicates whether to highlight the errors */
  shouldHighlightErrors?: boolean;
  /** Whether to show agree send email checkbox */
  showAgreeSendEmailInput?: boolean;
  /** Whether to show agree send sms checkbox */
  showAgreeSendSMSInput?: boolean;
  /** Whether to show the email input */
  showEmailInput?: boolean;
  /** Whether to show the phone input */
  showPhoneInput?: boolean;
  /** Whether to show the travel planner toggle */
  showTravelPlannerToggle?: boolean;
  /** The position of the tooltip */
  tooltipPosition?: TooltipPosition;
  /** The uniquePaxHash of the passenger as travel planner  */
  travelPlannerId?: string;
  /** The validation mode for the form */
  validationMode?: Mode;
}

const EditRoomOccupants = ({
  agreeSendEmailText,
  agreeSendSMSText,
  availableAddressCountries = [],
  className,
  disableEditDoB = false,
  disableEditNames = false,
  disclaimerText,
  emailAndPhoneMandatory = false,
  hideAddress = false,
  hideAge = false,
  isFetching = false,
  isLoading = false,
  onChange,
  onTravelPlannerToggle,
  onValidation,
  passengers = [],
  roomName,
  roomNumber,
  shouldClear = false,
  shouldHighlightErrors = false,
  showAgreeSendEmailInput = false,
  showAgreeSendSMSInput = false,
  showEmailInput = false,
  showPhoneInput = false,
  showTravelPlannerToggle = false,
  selectedCompanionIdsByPaxHash,
  tooltipPosition,
  travelPlannerId,
  onSelectCompanionByPaxHash,
  validationMode = 'onBlur',
}: EditRoomOccupantsProps) => {
  const { t } = useI18n();
  const [occupants, setOccupants] = useState<Content.Properties.Passenger[]>(
    passengers || [],
  );
  const [firstPassengerLastName, setFirstPassengerLastName] = useState<
    string | undefined
  >();
  const [isSameLastName, setIsSameLastName] = useState(false);
  const [editedRoomOccupants, setEditedRoomOccupants] = useState<
    EditedPassenger[]
  >([]);
  const [
    localSelectedCompanionIdsByPaxHash,
    setLocalSelectedCompanionIdsByPaxHash,
  ] = useState<Record<string, string>>({});
  const [validOccupants, setValidOccupants] = useState(
    passengers.map(passenger => ({
      id: passenger.uniquePaxHash,
      isCustomer: passenger.isCustomer,
      isValid: passenger.isCustomer,
    })),
  );

  const effectiveSelectedCompanionIdsByPaxHash =
    selectedCompanionIdsByPaxHash ?? localSelectedCompanionIdsByPaxHash;

  const handleSelectCompanionByPaxHash = (
    uniquePaxHash: string,
    companionId: string | null,
  ) => {
    if (onSelectCompanionByPaxHash) {
      onSelectCompanionByPaxHash(uniquePaxHash, companionId);

      return;
    }

    setLocalSelectedCompanionIdsByPaxHash(previousValue => {
      const nextValue = { ...previousValue };

      if (companionId) {
        nextValue[uniquePaxHash] = companionId;
      } else {
        delete nextValue[uniquePaxHash];
      }

      return nextValue;
    });
  };

  const handleEditPaxDetails = (editedPassenger: EditedPassenger) => {
    setEditedRoomOccupants(previous => {
      const filteredOccupants = previous.filter(
        passenger => passenger.uniquePaxHash !== editedPassenger.uniquePaxHash,
      );

      return [...filteredOccupants, editedPassenger];
    });
  };

  useEffect(() => {
    if (!occupants?.length) return;

    const editedFirstPassenger = editedRoomOccupants.find(
      passenger => passenger.uniquePaxHash === occupants[0].uniquePaxHash,
    );
    const lastName =
      editedFirstPassenger?.updatedFields?.lastName ?? occupants[0].lastName;

    setFirstPassengerLastName(isSameLastName ? lastName : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSameLastName]);

  useEffect(() => {
    if (!shouldClear) return;

    setIsSameLastName(false);

    if (!onSelectCompanionByPaxHash) {
      setLocalSelectedCompanionIdsByPaxHash({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClear]);

  useEffect(() => {
    if (!passengers) return;

    // Check if actual passenger data changed
    const hasChanged = JSON.stringify(passengers) !== JSON.stringify(occupants);

    if (hasChanged) {
      setOccupants(passengers);

      const hasSameLastName =
        passengers.length > 1 &&
        passengers.every(
          passenger =>
            passenger.lastName && passenger.lastName === passengers[0].lastName,
        );

      setIsSameLastName(hasSameLastName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passengers]);

  useEffect(() => {
    if (onSelectCompanionByPaxHash) return;

    const occupantHashes = new Set(
      occupants.map(occupant => occupant.uniquePaxHash),
    );

    setLocalSelectedCompanionIdsByPaxHash(previousValue => {
      const nextValue: Record<string, string> = {};

      Object.entries(previousValue).forEach(([paxHash, companionId]) => {
        if (occupantHashes.has(paxHash)) {
          nextValue[paxHash] = companionId;
        }
      });

      return nextValue;
    });
  }, [occupants, onSelectCompanionByPaxHash]);

  useEffect(() => {
    onChange?.(editedRoomOccupants);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedRoomOccupants]);

  useEffect(() => {
    setValidOccupants(previousValue =>
      previousValue.map(item => ({
        ...item,
        isCustomer: item.id === travelPlannerId,
        isValid: item.id === travelPlannerId || item.isValid,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelPlannerId]);

  useEffect(() => {
    const isValid = !validOccupants.find(
      item => !item.isCustomer && !item.isValid,
    );

    onValidation?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validOccupants]);

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, className, styles.isLoading)}
      >
        <div className={styles.header}>
          <div className={styles.headlineWrapper}>
            <div className={styles.headline}>
              <Icon name="Bed" size={24} />

              <SkeletonLoader
                className={classNames(styles.roomName, styles.loader)}
                height="24px"
                width="60px"
              />
            </div>

            <SkeletonLoader
              className={classNames(styles.roomName, styles.loader)}
              height="24px"
              width="200px"
            />
          </div>

          <div className={styles.toggleSwitch}>
            <SkeletonLoader
              className={styles.loader}
              height="24px"
              width="180px"
            />
          </div>
        </div>

        {Array.from({ length: 2 }).map((_, passengerIndex) => (
          <EditPaxDetails key={passengerIndex} isLoading />
        ))}
      </div>
    );
  }

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.header}>
        <div className={styles.headlineWrapper}>
          <div className={styles.headline}>
            <Icon name="Bed" size={24} />

            {roomNumber && (
              <Typography tagStyle="bodyMediumBold">
                {`${t('items.modules.bookingFlow.room')} ${roomNumber}:`}
              </Typography>
            )}

            {shouldHighlightErrors && (
              <Typography
                className={styles.error}
                text={`*${t('items.modules.bookingFlow.required')}`}
              />
            )}
          </div>

          {roomName && (
            <Typography className={styles.roomName} tagStyle="bodyMedium">
              {roomName}
            </Typography>
          )}
        </div>

        <div className={styles.toggleSwitch}>
          {isFetching ? (
            <SkeletonLoader
              className={styles.loader}
              height="24px"
              width="180px"
            />
          ) : (
            <ToggleSwitch
              checked={isSameLastName}
              label={t('items.modules.bookingFlow.sameLastNameToggle')}
              onClick={() => setIsSameLastName(previousValue => !previousValue)}
            />
          )}
        </div>
      </div>

      {occupants.map((passenger, passengerIndex) => (
        <EditPaxDetails
          key={passenger.uniquePaxHash}
          agreeSendEmailText={agreeSendEmailText}
          agreeSendSMSText={agreeSendSMSText}
          availableAddressCountries={availableAddressCountries}
          disableEditDoB={disableEditDoB}
          disableEditNames={disableEditNames}
          disclaimerText={disclaimerText}
          emailAndPhoneMandatory={emailAndPhoneMandatory}
          firstPassengerLastName={firstPassengerLastName}
          hideAddress={hideAddress}
          hideAge={hideAge}
          hideAgreeSendEmail={!showAgreeSendEmailInput}
          hideAgreeSendSMS={!showAgreeSendSMSInput}
          hideEmail={!showEmailInput}
          hidePhone={!showPhoneInput}
          hideTravelPlannerToggle={!showTravelPlannerToggle}
          isFirstPassenger={passengerIndex === 0}
          isLoading={isFetching}
          onChange={handleEditPaxDetails}
          onSelectCompanion={companionId => {
            handleSelectCompanionByPaxHash(
              passenger.uniquePaxHash,
              companionId,
            );
          }}
          onTravelPlannerToggle={value => {
            onTravelPlannerToggle?.(value);
            setIsSameLastName(false);
          }}
          onValidation={value => {
            setValidOccupants(previousValue =>
              previousValue.map((item, index) => ({
                ...item,
                isValid: index === passengerIndex ? value : item.isValid,
              })),
            );
          }}
          passenger={passenger}
          selectedCompanionId={
            effectiveSelectedCompanionIdsByPaxHash[passenger.uniquePaxHash]
          }
          shouldClear={shouldClear}
          shouldHighlightErrors={shouldHighlightErrors}
          tooltipPosition={tooltipPosition}
          travelPlannerId={travelPlannerId}
          usedCompanionIds={Object.entries(
            effectiveSelectedCompanionIdsByPaxHash,
          )
            .filter(([paxHash]) => paxHash !== passenger.uniquePaxHash)
            .map(([, companionId]) => companionId)}
          validationMode={validationMode}
        />
      ))}
    </div>
  );
};

EditRoomOccupants.displayName = 'EditRoomOccupants';

export default EditRoomOccupants;
